import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

// Inline keyframes injected once
const STYLE_ID = 'alarm-ack-keyframes';

function injectKeyframes() {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');

  s.id = STYLE_ID;
  s.textContent = `
    @keyframes ackCircle {
      0%   { transform: scale(0); opacity: 0; }
      60%  { transform: scale(1.15); opacity: 1; }
      100% { transform: scale(1); opacity: 1; }
    }
    @keyframes ackTick {
      0%   { stroke-dashoffset: 60; opacity: 0; }
      40%  { opacity: 1; }
      100% { stroke-dashoffset: 0; opacity: 1; }
    }
    @keyframes ackFadeUp {
      from { opacity: 0; transform: translateY(12px); }
      to   { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(s);
}

export default function AlarmAckDialog({ open, title, onClose }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (open) {
      injectKeyframes();
      // tiny delay so the animation re-triggers even if dialog re-opens
      const t = setTimeout(() => setShow(true), 30);

      return () => clearTimeout(t);
    } else {
      setShow(false);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '20px',
          padding: '28px 24px 20px',
          textAlign: 'center',
          minWidth: 280,
          maxWidth: 340,
          boxShadow: '0 8px 32px rgba(45,27,105,0.18)',
        },
      }}
    >
      <DialogContent sx={{ padding: 0 }}>
        {/* Animated tick circle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: '18px' }}>
          <svg width="90" height="90" viewBox="0 0 90 90">
            <circle
              cx="45" cy="45" r="42"
              fill="#e8f5e9"
              stroke="#2e7d32"
              strokeWidth="3"
              style={show ? {
                animation: 'ackCircle 0.45s cubic-bezier(.36,.07,.19,.97) both',
              } : { opacity: 0 }}
            />
            <polyline
              points="24,47 38,61 66,33"
              fill="none"
              stroke="#2e7d32"
              strokeWidth="5"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="60"
              strokeDashoffset="60"
              style={show ? {
                animation: 'ackTick 0.4s ease 0.3s forwards',
              } : {}}
            />
          </svg>
        </Box>

        {/* Title */}
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '1.05rem',
            color: '#1b5e20',
            mb: '8px',
            opacity: show ? 1 : 0,
            animation: show ? 'ackFadeUp 0.35s ease 0.55s both' : 'none',
          }}
        >
          Alarm Acknowledged
        </Typography>

        {/* Alarm name */}
        {title && (
          <Typography
            sx={{
              fontSize: '0.85rem',
              color: '#555',
              mb: '4px',
              opacity: show ? 1 : 0,
              animation: show ? 'ackFadeUp 0.35s ease 0.65s both' : 'none',
            }}
          >
            {title}
          </Typography>
        )}

        <Typography
          sx={{
            fontSize: '0.8rem',
            color: '#888',
            mb: '20px',
            opacity: show ? 1 : 0,
            animation: show ? 'ackFadeUp 0.35s ease 0.75s both' : 'none',
          }}
        >
          You have acknowledged this alarm.
        </Typography>

        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            borderRadius: '30px',
            background: 'linear-gradient(135deg, #2d1b69, #0d7377)',
            textTransform: 'none',
            fontWeight: 600,
            paddingX: '32px',
            fontSize: '0.9rem',
            opacity: show ? 1 : 0,
            animation: show ? 'ackFadeUp 0.35s ease 0.85s both' : 'none',
            '&:hover': { background: 'linear-gradient(135deg, #3d2b89, #1d9397)' },
          }}
        >
          OK
        </Button>
      </DialogContent>
    </Dialog>
  );
}
